import { useQuery } from '@tanstack/react-query';

import {
  type ExecuteSavedQueryFieldValue,
  executeSavedQuery,
} from '../../../api/saved-queries-api';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type {
  SnowflakeConfig,
  SnowflakeConfigField,
} from '../../PageConfigProvider/types/item';
import { DataProviderQueryStatus } from '../types';
import { getReportParameterFieldValue } from './getReportParameterFieldValue';

function manualValueToFieldValue(
  field: SnowflakeConfigField
): ExecuteSavedQueryFieldValue | undefined {
  const raw = field.value;
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return undefined;

  const type = field.fieldType ?? 'string';
  if (type === 'number') {
    const num = Number(trimmed);
    return Number.isNaN(num) ? undefined : num;
  }
  if (type === 'number[]') {
    const parts = trimmed
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    const nums = parts.map((p) => Number(p)).filter((n) => !Number.isNaN(n));
    return nums.length > 0 ? nums : undefined;
  }
  if (type === 'string[]') {
    const values = trimmed
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    return values.length > 0 ? values : undefined;
  }
  return trimmed;
}

function isResolvedValueEmpty(
  value: ExecuteSavedQueryFieldValue | undefined
): boolean {
  if (value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return Number.isNaN(value);
  return value.length === 0;
}

function allRequiredFieldsSet(
  config: SnowflakeConfig,
  resolvedValues: Record<string, ExecuteSavedQueryFieldValue | undefined>
): boolean {
  for (const field of config.fields) {
    if (field.required === false) continue;
    if (field.subFields !== undefined && field.subFields.length > 0) {
      const allSubFieldsSet = field.subFields.every(
        (sub) => !isResolvedValueEmpty(resolvedValues[sub.name])
      );
      if (!allSubFieldsSet) return false;
    } else {
      if (isResolvedValueEmpty(resolvedValues[field.name])) return false;
    }
  }
  return true;
}

export type SnowflakeSavedQueryResult = {
  data: Record<string, unknown>[] | undefined;
  status: DataProviderQueryStatus;
  queryStatus: 'pending' | 'error' | 'success';
  error: Error | undefined;
  enabled: boolean;
};

export function useSnowflakeSavedQuery(
  config: SnowflakeConfig | null | undefined
): SnowflakeSavedQueryResult {
  const { config: tabConfig } = useTabContentConfig();
  const { activeFilterMap, filterState } = useFilterContext();
  const reportParameters = tabConfig.reportParameters ?? [];

  const resolvedValues: Record<
    string,
    ExecuteSavedQueryFieldValue | undefined
  > = (() => {
    if (!config?.fields?.length) return {};

    const result: Record<string, ExecuteSavedQueryFieldValue | undefined> = {};

    for (const field of config.fields) {
      if (field.subFields !== undefined && field.subFields.length > 0) {
        if (field.parameterId !== undefined && field.parameterId !== '') {
          const parameter = reportParameters.find(
            (p) => p.parameterId === field.parameterId
          );
          if (parameter) {
            const filterMap = parameter.isPrimary
              ? activeFilterMap
              : filterState.groupMap?.get(field.parameterId);
            const filter = filterMap?.get(parameter.filterId);
            for (const sub of field.subFields) {
              result[sub.name] = getReportParameterFieldValue(
                parameter,
                filter,
                sub.fieldType,
                sub.selectionLevelIds,
                sub.extractMode
              );
            }
          }
        }
      } else if (field.parameterId !== undefined && field.parameterId !== '') {
        const parameter = reportParameters.find(
          (p) => p.parameterId === field.parameterId
        );
        if (!parameter) {
          result[field.name] = undefined;
        } else {
          const filterMap = parameter.isPrimary
            ? activeFilterMap
            : filterState.groupMap?.get(field.parameterId);
          const filter = filterMap?.get(parameter.filterId);
          result[field.name] = getReportParameterFieldValue(
            parameter,
            filter,
            field.fieldType ?? 'string',
            field.selectionLevelIds
          );
        }
      } else {
        result[field.name] = manualValueToFieldValue(field);
      }
    }
    return result;
  })();

  const fieldValues: Record<string, ExecuteSavedQueryFieldValue> = (() => {
    const entries: Array<[string, ExecuteSavedQueryFieldValue]> = [];
    for (const field of config?.fields ?? []) {
      if (field.subFields !== undefined && field.subFields.length > 0) {
        for (const sub of field.subFields) {
          const value = resolvedValues[sub.name];
          if (value !== undefined && !isResolvedValueEmpty(value)) {
            entries.push([sub.name, value]);
          }
        }
      } else {
        const value = resolvedValues[field.name];
        if (value !== undefined && !isResolvedValueEmpty(value)) {
          entries.push([field.name, value]);
        }
      }
    }
    return Object.fromEntries(entries);
  })();

  const enabled =
    Boolean(config?.savedQueryId) &&
    (!config?.fields?.length || allRequiredFieldsSet(config, resolvedValues));

  const { data, status, error } = useQuery({
    queryKey: ['snowflake-chart', config?.savedQueryId, fieldValues],
    queryFn: async () => {
      if (!config?.savedQueryId) return undefined;
      const result = await executeSavedQuery(config.savedQueryId, fieldValues);
      return (result ?? []) as Record<string, unknown>[];
    },
    enabled,
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (failureCount >= 2) return false;
      const status = (err as Error & { status?: number })?.status;
      if (!status) return true;
      if (status >= 500) return true;
      return status === 408 || status === 429;
    },
    retryDelay: (attempt) => 1000 * 2 ** attempt,
  });

  const mappedStatus = (() => {
    if (!enabled) return DataProviderQueryStatus.Success;
    if (status === 'pending') return DataProviderQueryStatus.Fetching;
    if (status === 'error') return DataProviderQueryStatus.Error;
    return DataProviderQueryStatus.Success;
  })();

  return {
    data,
    status: mappedStatus,
    queryStatus: status,
    error: error instanceof Error ? error : undefined,
    enabled,
  };
}
